import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../../utils/seo';

// ===== チームリーダーシップ研修 詳細 全面作り直し（Implementer C スコープ） =====
// デザイン土台: home / about/overview の新スタイル（紺#1a2942 / 真鍮金 / NavyBand / BTN-08 / 細罫 / 金差し色見出し）。
// 実データ変更禁止・実文は現行working tree実文のみ（旧page.tsx由来）・創作禁止。料金400,000円/1日3時間/10-20名は不変。

function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";
const TEXT_ARROW = "inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] tracking-[0.06em] border-b border-[#a38e65] pb-1 hover:text-[#8f7a56] transition-colors duration-300";

const OVERVIEW = [
  '執事長（Head Butler）は、複数の執事やスタッフを統括し、邸宅全体のサービス品質を保つ責任を負います。限られた人数で多様な業務を遂行し、予期せぬ事態にも冷静に対処する。そのマネジメント手法には、現代の企業リーダーが学ぶべき要素が数多く含まれています。',
  '本研修では、執事のリーダーシップを理論と実践の両面から学びます。サーバントリーダーシップの原則、チーム編成とタスク分配、効果的な指示の出し方、部下の育成方法、コンフリクトの早期発見と解決、緊急時の意思決定プロセスまで、実務に直結する内容を習得。グループワークとロールプレイを通じて、実践的なリーダーシップスキルを体得します。',
  'ホテル・旅館の管理職、レストランのマネージャー、富裕層向けサービス業の責任者など、チームを率いる立場の方に最適な研修です。',
];

const FEATURES = [
  { icon: 'ri-lightbulb-line', t: '実践的理論', d: '執事の現場経験に基づく理論を学びます。' },
  { icon: 'ri-team-line', t: 'グループワーク', d: '実践演習で体得します。' },
  { icon: 'ri-rocket-line', t: '即実務応用', d: '翌日から使えるスキルが身につきます。' },
];

const CURRICULUM = [
  { part: '第1部', time: '45分', title: 'リーダーシップの基礎理論', desc: '執事におけるリーダーシップの定義、サーバントリーダーシップの原則、権威と信頼の違い、リーダーに求められる資質を学びます。執事長が実践する「支える」リーダーシップの本質を理解し、現代の企業組織での応用方法を習得します。理論だけでなく、実際の執事の現場での事例を通じて、効果的なリーダーシップのあり方を深く学びます。' },
  { part: '第2部', time: '45分', title: 'チームビルディング', desc: '効果的なチーム編成の方法、メンバーの強みの見極め方、タスク分配の最適化、チームの結束力を高める技術を習得します。執事が実践する「適材適所」の人材配置、個々の能力を最大化するマネジメント手法、チーム全体のパフォーマンスを向上させる具体的な方法を学びます。グループワークを通じて実践的なスキルを身につけます。' },
  { part: '第3部', time: '45分', title: '効果的なコミュニケーション', desc: '明確な指示の出し方、傾聴技術とフィードバック、非言語コミュニケーションの活用、報告・連絡・相談の仕組みづくりを学びます。執事が実践する「相手の立場に立った」コミュニケーション技術、信頼関係を築く対話の方法、効果的な情報共有の仕組みなど、チームの連携を強化するコミュニケーションスキルを習得します。' },
  { part: '第4部', time: '45分', title: 'コンフリクト解決とケーススタディ', desc: '対立の早期発見、建設的な議論の促進、調停と問題解決の技術、実際のケースを用いた演習で実践力を養います。執事が実践する「冷静な判断」と「公平な調整」の技術、感情的な対立を建設的な議論に導く方法、チーム内の問題を成長の機会に変える手法を学びます。実際のケーススタディを通じて、現場で使える問題解決スキルを身につけます。' },
];

const RECOMMENDS = [
  '高級ホテル・旅館の管理職',
  'レストランのマネージャー・店長',
  'コンシェルジュ部門の責任者',
  '富裕層向けサービス業のチームリーダー',
  '接客業の新任管理職',
  '多様なスタッフを統括する立場の方',
  'チーム連携に課題を感じている部門',
  'サービス品質向上を目指す組織',
];

const OUTCOMES = [
  { t: '実践的リーダーシップスキル', d: '理論だけでなく、現場ですぐに使える具体的な手法を習得できます。執事の現場経験に基づく実践的なリーダーシップ技術を身につけます。' },
  { t: 'チームマネジメント力の向上', d: 'メンバーの能力を最大化し、チーム全体のパフォーマンスを高める技術を身につけます。適材適所の人材配置と効果的なタスク分配を習得します。' },
  { t: '問題解決能力の強化', d: 'コンフリクトを建設的に解決し、チームの成長につなげる力を養います。冷静な判断力と公平な調整技術を習得します。' },
  { t: 'コミュニケーション技術の向上', d: '明確な指示、的確なフィードバック、効果的な傾聴の技術を習得します。信頼関係を築く対話の方法を身につけます。' },
];

const SPECS = [
  { k: '研修時間', v: '1日3時間（午前/午後選択可）' },
  { k: '対象人数', v: '10-20名' },
  { k: '対象レベル', v: '中級〜上級（管理職・リーダー層）' },
  { k: '実施場所', v: '貴社会議室または当協会会場' },
  { k: 'カスタマイズ', v: '内容調整可能（別途相談）' },
];

const INCLUDES = ['研修プログラム実施', 'テキスト教材（参加者人数分）', '修了証（参加者全員）', '講師交通費（都内の場合）', '事後フォローアップ（1回）'];

const FLOW = [
  { t: 'お問い合わせ・資料請求', d: 'まずはお電話またはWebフォームにてお気軽にお問い合わせください。研修内容の詳細資料をお送りいたします。' },
  { t: 'ヒアリング・ご提案', d: '貴社の課題やご要望を詳しくお聞かせください。最適な研修プログラムをカスタマイズしてご提案いたします。' },
  { t: 'お見積り・契約', d: '研修内容に基づいた詳細なお見積りをご提示いたします。ご納得いただけましたら契約を締結いたします。' },
  { t: '事前準備・調整', d: '研修実施に向けて、会場の確認、参加者の調整、教材の準備など、詳細な打ち合わせを行います。' },
  { t: '研修実施', d: '経験豊富な講師陣による質の高い研修を実施いたします。参加者の理解度に合わせて柔軟に対応いたします。' },
  { t: 'フォローアップ', d: '研修後のアンケート実施、効果測定、継続的なサポートなど、研修効果の定着をお手伝いいたします。' },
];

const RELATED = [
  { label: 'コミュニケーション技術研修', href: '/seminars/corporate/communication-skills', desc: '執事に学ぶ傾聴力・表現力・非言語コミュニケーションの技術' },
  { label: '危機管理研修', href: '/seminars/corporate/crisis-management', desc: '執事に学ぶリスクアセスメントと緊急時対応の技術' },
  { label: '企業向け研修プログラム一覧', href: '/seminars', desc: 'すべての研修プログラムを確認し、最適な組み合わせを見つける' },
];

export default function TeamLeadershipPage() {
  useEffect(() => {
    document.title = 'チームリーダーシップ研修｜リーダー育成｜全日本執事協会';
    updateSEO({
      title: 'チームリーダーシップ研修｜リーダー育成｜全日本執事協会',
      description: '全日本執事協会のチームリーダーシップ研修。効果的なリーダーシップスキルとチーム管理能力を身につけ、組織力向上を目指す企業研修プログラムです。',
      canonical: 'https://butler-association.jp/seminars/corporate/team-leadership',
    });
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'セミナー・イベント', href: '/seminars' },
    { label: 'チームリーダーシップ研修' },
  ];

  return (
    <Layout>
      {/* ===== ヒーロー — 全幅ビルボード型（画像+紺オーバーレイ+左テキスト）。由来: about/overview ヒーロー ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/generated/jba-corp-team-leadership.webp" alt="チームリーダーシップ研修で受講者にチーム運営の技術を指導する執事" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.3em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Team Leadership</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.2vw,42px)] leading-[1.4] tracking-[0.04em] mb-6">チームリーダーシップ研修</h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              執事が長年実践してきたチームマネジメントの手法を、現代の企業組織に応用する実践的プログラムです。執事長は複数のスタッフを統括し、邸宅全体のサービスを統括する立場。限られたリソースで最高の成果を出すためのリーダーシップスキルは、現代の企業マネジメントに直接応用できます。本研修では、リーダーシップ理論、チームビルディング、効果的なコミュニケーション、コンフリクト解決まで、管理職に必要な実践的スキルを体系的に習得します。
            </p>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      {/* 注記（実文保持・装飾警告ボックスを上質な細罫ノートへ） */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-10">
          <p className="border-l-2 border-[#a38e65] bg-[#f8f6f0] px-5 py-4 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">
            ⚠️ 本研修はスキル習得を目的としており、業績向上や昇進を保証するものではありません。
          </p>
        </div>
      </div>

      {/* ===== I. この研修について — 2カラム（本文3段+側画像）+ 3つの特徴 ===== */}
      <NavyBand no="I" ja="この研修について" en="Overview" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_360px] gap-10 lg:gap-14 items-start mb-12 sm:mb-14">
            <div>
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-6">
                執事長に学ぶ、<span className="text-[#a38e65]">支えるリーダーシップ</span>
              </h3>
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                {OVERVIEW.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
            <img src="/images/salon0926/20260912_104529.jpg" alt="昼の光が差す執事サロンの正餐の間" className="w-full h-64 lg:h-[380px] object-cover border border-[#a38e65]/30" />
          </div>
          <div className="grid sm:grid-cols-3 border-t-2 border-[#1a2942]">
            {FEATURES.map((f) => (
              <div key={f.t} className="flex flex-col gap-4 border-b border-[#e2ddd2] sm:border-b-0 sm:border-r last:border-r-0 py-7 sm:px-6 first:sm:pl-0">
                <span className="inline-flex items-center justify-center w-12 h-12 border border-[#a38e65]/50 text-[#a38e65]"><i className={`${f.icon} text-2xl`}></i></span>
                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">{f.t}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== II. 研修カリキュラム = 新バリエーション①（番号付き時間バッジ・エディトリアル）
           由来注記: home IV 全幅エディトリアル行型の「1行1項目・列整列」を、左に部番号+45分バッジ・右に本文の
           カリキュラム時系列へ翻案。トップ/概要に同型は無いため新造。 ===== */}
      <NavyBand no="II" ja="研修カリキュラム（1日3時間）" en="Curriculum" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 text-left">
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
              チームリーダーシップ研修では、執事の現場経験に基づく4つの分野を体系的に学びます。各部は実践的な内容で構成され、知識だけでなく実際のマネジメント技術も習得できます。
            </p>
          </div>
          <img src="/images/generated/jba-fs-225-seminars_corporate_team-leadership-v5.webp" alt="チームリーダーシップ研修のカリキュラム教材と講義風景" className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10" />
          <div className="border-t-2 border-[#1a2942]">
            {CURRICULUM.map((c) => (
              <div key={c.part} className="flex flex-col md:flex-row gap-4 md:gap-10 border-b border-[#e2ddd2] py-7">
                <div className="md:w-[220px] md:shrink-0">
                  <div className="flex items-center gap-3">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl leading-none">{c.part}</span>
                    <span className="inline-block border border-[#a38e65]/60 text-[#8f7a56] text-xs tracking-[0.06em] px-2.5 py-1 font-['Noto_Serif_JP',serif]">{c.time}</span>
                  </div>
                  <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mt-3">{c.title}</h4>
                </div>
                <p className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95]">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== III. こんな企業・部門におすすめ — 2列細罫チェックリスト ===== */}
      <NavyBand no="III" ja="こんな企業・部門におすすめ" en="Ideal For" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid sm:grid-cols-2 border-t border-[#e2ddd2]">
            {RECOMMENDS.map((r) => (
              <div key={r} className="flex items-center gap-4 border-b border-[#e2ddd2] py-5 sm:px-5 first:sm:pl-0">
                <i className="ri-check-line text-xl text-[#a38e65] shrink-0"></i>
                <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942]">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== IV. 研修で得られる成果 — 番号付き紺カード + 上部画像 ===== */}
      <NavyBand no="IV" ja="研修で得られる成果" en="Outcomes" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <img src="/images/generated/jba-p-040-team-exercise.webp" alt="研修を経て高まったチームマネジメント力を示す受講者の様子" className="w-full aspect-[21/9] h-auto object-cover object-top border border-[#a38e65]/30 mb-10" />
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {OUTCOMES.map((o, i) => (
              <div key={o.t} className="bg-[#1a2942] border border-[#a38e65]/45 p-7 sm:p-8 flex flex-col">
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none">{String(i + 1).padStart(2, '0')}</span>
                  <h4 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-xl leading-[1.5]">{o.t}</h4>
                </div>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.95]">{o.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== V. 実施形式と料金 = 新バリエーション②（大数値+細罫スペック表+含有グリッド）
           由来注記: home IV の Fee 大数値強調 + home VII 細罫テーブルを組み合わせ、料金主役の
           スペックブロックへ再構成。単独の料金主役ブロックはトップに無いため新造。 ===== */}
      <NavyBand no="V" ja="実施形式と料金" en="Details & Fee" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[360px_1fr] gap-10 lg:gap-14 items-start">
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 text-center">
              <div className="font-['Noto_Serif_JP',serif] text-sm text-[#d7b779] tracking-[0.1em] mb-3">受講料（税込）</div>
              <div className="font-['Playfair_Display',serif] text-[#f8f6f0] text-5xl sm:text-6xl leading-none">400,000<span className="text-2xl ml-1">円</span></div>
              <div className="w-11 h-px bg-[#a38e65] mx-auto mt-6 mb-5"></div>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">1日3時間・10-20名でのご提供です。</p>
            </div>
            <div>
              <dl className="border-t-2 border-[#1a2942]">
                {SPECS.map((s) => (
                  <div key={s.k} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                    <dt className="sm:w-[160px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{s.k}</dt>
                    <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{s.v}</dd>
                  </div>
                ))}
              </dl>
              <h4 className="jba-display font-medium text-[#1a2942] text-lg mt-10 mb-5">料金に含まれるもの</h4>
              <ul className="grid sm:grid-cols-2 gap-x-8">
                {INCLUDES.map((inc) => (
                  <li key={inc} className="flex items-start gap-3 border-b border-[#e2ddd2] py-3 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><i className="ri-check-line text-[#a38e65]"></i><span>{inc}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== VI. お申込みの流れ — 番号付き縦ステッパー ===== */}
      <NavyBand no="VI" ja="お申込みの流れ" en="How to Apply" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <ol className="relative border-l border-[#e2ddd2] ml-5 max-w-4xl">
            {FLOW.map((s, i) => (
              <li key={s.t} className="relative pl-8 sm:pl-10 pb-9 last:pb-0">
                <span className="absolute -left-[19px] top-0 w-9 h-9 rounded-full bg-[#1a2942] text-[#d7b779] flex items-center justify-center font-['Playfair_Display',serif] text-sm border border-[#a38e65]/50">{i + 1}</span>
                <h4 className="jba-display font-medium text-[#1a2942] text-lg mb-1.5">{s.t}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
              </li>
            ))}
          </ol>
          <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 mt-12 text-center">
            <h4 className="jba-display font-medium text-xl sm:text-2xl text-[#f8f6f0] mb-3">お問い合わせ・お申込み</h4>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] mb-7 max-w-2xl mx-auto leading-[1.9]">チームリーダーシップ研修についてのご質問やお申込みは、お気軽にお問い合わせください。</p>
            <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ・お申込みはこちら</a>
          </div>
        </div>
      </section>

      {/* ===== 関連研修プログラム — 正しい行き先・正しい名称のカード（宙リンク/取り違えゼロ）===== */}
      <NavyBand no="VII" ja="関連研修プログラム" en="Related Programs" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-8">
            {RELATED.map((r) => (
              <a key={r.href} href={r.href} className="group bg-white border border-[#e2ddd2] p-8 flex flex-col hover:border-[#a38e65]/70 transition-colors duration-300">
                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mb-3 group-hover:text-[#8f7a56] transition-colors">{r.label}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9] mb-6">{r.desc}</p>
                <span className={`${TEXT_ARROW} mt-auto self-start`}>詳細を見る <span className="text-[#a38e65]">→</span></span>
              </a>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href="/seminars" className={BTN08_ON_LIGHT}>セミナー・イベント一覧へ戻る</a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
